import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorReporting } from '../request-options.ts';
import { CONTRACT_HEADER } from '../handlers/type-normalization-handler.ts';
import { camelizeAttributes } from '../utils/camelize-attributes.ts';
import { buildQueryRequestUrl } from '../utils/request-url.ts';

/**
 * PATCH /people/:id/contribution-account — reorder the person's
 * contribution accounts. Body is the legacy JSON:API shape the server
 * expects (singular `person` type with snake_case attribute key).
 */
export function updateContributionAccountRequest(
  personId: string,
  accountIds: string[]
): RequestInfo {
  const opts: QueryUrlOptions = {
    identifier: { type: 'people' },
    op: 'query',
    resourcePath: 'people',
  };
  return {
    url: `${buildBaseURL(opts)}/${personId}/contribution-account`,
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: 'person',
        id: personId,
        attributes: { account_order: accountIds },
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

/** A single alert from GET /api/v2/people/:id/slr-alerts. */
export interface SlrAlert {
  type: string;
  category: 'account-setup' | 'account-ineligible' | 'plan-requirement' | 'default';
  display_mode: 'single' | 'grouped';
  /** `loan-identity` asks the member to verify held loans on their own "Verify Your Loans" page. */
  resolution_type: 'upload' | 'dropdown' | 'contact-support' | 'loan-identity' | 'none';
  /** Only a `temporary` alert may be dismissed. Absent from older servers; read absence as `persisting`. */
  persistence?: 'persisting' | 'temporary';
  /** Whether this alert alone stops the next contribution. Absent from older servers; read absence as `true`. */
  blocks_contribution?: boolean;
  title: string;
  description: string;
  /** Display order, ascending — the server sorts by it and callers keep that order. */
  priority: number;
  /** Passed through untouched, so its keys keep the wire's dasherized spelling. */
  metadata: Record<string, unknown>;
}

/**
 * The alerts payload as this app consumes it.
 *
 * Kept in the v1 shape — flat, snake_case — deliberately: the kit returns a
 * JSON:API document with dasherized keys, and {@link parseSlrAlerts} maps onto
 * this so the components reading it did not have to change.
 */
export interface SlrAlertsResponse {
  page_state: string;
  alerts: SlrAlert[];
  contribution_account: {
    id: string;
    servicer_name: string | null;
    account_number: string | null;
    balance: number | null;
    is_pif: boolean;
    servicer_address_type: string | null;
  } | null;
}

/** The kit's document shape for one `slr-alert-reports` resource. */
interface SlrAlertsDocument {
  data?: {
    id?: string;
    attributes?: Record<string, unknown>;
  } | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/**
 * Maps the kit's JSON:API document onto {@link SlrAlertsResponse}.
 *
 * Attribute keys arrive dasherized. The apps negotiate `x-json-api-key-format:
 * camelized`, but that is a jsonapi-resources feature the kit does not implement
 * — it always emits the target contract's dasherized keys — and the response
 * pipeline skips its own camelization whenever that negotiation is in play. So a
 * kit consumer reads dasherized names itself, the same way the match-plans
 * builder does.
 *
 * Returns the empty-but-valid shape for a missing document rather than throwing,
 * because every call site treats this read as non-critical (see
 * SkipErrorReporting on the request).
 */
export function parseSlrAlerts(content: unknown): SlrAlertsResponse {
  const attributes = asRecord((content as SlrAlertsDocument)?.data?.attributes) ?? {};
  const account = asRecord(attributes['contribution-account']);

  return {
    page_state: (attributes['page-state'] as string) ?? '',
    alerts: (Array.isArray(attributes['alerts']) ? attributes['alerts'] : []).map((raw) => {
      const alert = asRecord(raw) ?? {};
      return {
        type: alert['type'],
        category: alert['category'],
        display_mode: alert['display-mode'],
        resolution_type: alert['resolution-type'],
        ...(alert['persistence'] === undefined ? {} : { persistence: alert['persistence'] }),
        ...(alert['blocks-contribution'] === undefined
          ? {}
          : { blocks_contribution: alert['blocks-contribution'] }),
        title: alert['title'],
        description: alert['description'],
        priority: alert['priority'],
        metadata: alert['metadata'] ?? {},
      } as SlrAlert;
    }),
    contribution_account: account
      ? {
          id: account['id'] as string,
          servicer_name: (account['servicer-name'] as string | null) ?? null,
          account_number: (account['account-number'] as string | null) ?? null,
          balance: (account['balance'] as number | null) ?? null,
          is_pif: Boolean(account['is-pif']),
          servicer_address_type: (account['servicer-address-type'] as string | null) ?? null,
        }
      : null,
  };
}

/**
 * GET /api/v2/people/:id/slr-alerts — student-loan-repayment alerts for a person,
 * on the tio-api v2 kit.
 *
 * The kit returns a JSON:API document with dasherized attribute keys rather than
 * v1's flat snake_case payload, so callers parse the response through
 * {@link parseSlrAlerts} instead of casting `content`. That keeps
 * {@link SlrAlertsResponse} — and therefore every call site — unchanged.
 */
export function getSlrAlertsRequest(personId: string): RequestInfo {
  const opts: QueryUrlOptions = {
    identifier: { type: 'people' },
    op: 'query',
    resourcePath: 'api/v2/people',
  };
  return {
    url: `${buildBaseURL(opts)}/${personId}/slr-alerts`,
    method: 'GET',
    // No X-Tio-Contract header: this endpoint is kit-only, so its default IS the
    // target contract, and the header's other job — opting out of the
    // query-parameter rewrites — is moot for a request that sends none. If this
    // ever starts dual-serving, the builder declares the contract then.
    cacheOptions: { [SkipCache]: true },
    // Non-critical fetch whose failures must not surface globally (no
    // "Access denied" toast from the response handler): every call site
    // handles failure locally — fail-quiet [] on the dashboard widget,
    // outage notice on the reimagined pages. See SkipErrorReporting docs.
    options: {
      [SkipErrorReporting]: true,
    },
  };
}

/** One open servicer question on a member's document, as the person page shows it to any People admin. */
export interface PersonServicerQuestion {
  /** The question's id, the one `complete-on-behalf` takes. */
  id: string;
  documentId: string;
  /** The servicer name as the member's document printed it. */
  rawText: string | null;
}

interface PersonServicerQuestionsDocument {
  data?: { id?: unknown; attributes?: Record<string, unknown> }[] | null;
}

/**
 * GET /api/v2/people/:id/servicer-questions — every open servicer question on
 * one person's documents, newest first and unpaginated. Readable with
 * `admin.people`; answering one stays on the Servicer queue.
 *
 * @param personId the person whose page is on screen
 * @returns a request whose content {@link parsePersonServicerQuestions} reads
 * @throws 403 without `admin.people`; 404 when the person is not in the viewer's scope
 */
export function personServicerQuestionsRequest(personId: string): RequestInfo {
  return {
    url: buildQueryRequestUrl(
      'people',
      'api/v2/people',
      `${encodeURIComponent(personId)}/servicer-questions`
    ),
    method: 'GET',
    headers: new Headers({ [CONTRACT_HEADER]: '2' }),
    cacheOptions: { [SkipCache]: true },
    // The page words its own failure, so no global toast.
    options: { [SkipErrorReporting]: true },
  };
}

/**
 * Reads the questions document whichever key spelling the pipeline left, keeping
 * ids as strings and dropping an entry that names no document.
 *
 * @param content the document {@link personServicerQuestionsRequest} returned
 * @returns the questions in the order served; empty for a missing or malformed document
 */
export function parsePersonServicerQuestions(content: unknown): PersonServicerQuestion[] {
  const data = (content as PersonServicerQuestionsDocument | null)?.data;
  if (!Array.isArray(data)) return [];
  return data.flatMap((resource) => {
    const attrs = camelizeAttributes(resource?.attributes);
    const documentId = attrs['documentId'];
    if (resource?.id == null || documentId == null || documentId === '') return [];
    const rawText = attrs['rawText'];
    return [
      {
        id: String(resource.id),
        documentId: String(documentId),
        rawText: typeof rawText === 'string' ? rawText : null,
      },
    ];
  });
}
